import SolidPlaneDepartureAltIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureAltIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { fillArray } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import { useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useIntl } from 'react-intl'

const TimeLineContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: auto 1fr auto;
  column-gap: ${rem(8)};
  align-items: center;
`

const FlightInfoContainer = styled(Group)`
  width: 100%;
`

const FlightLineContainer = styled(Group)`
  width: 100%;
`

const FlightLine = styled.div`
  width: 100%;
  height: 1px;
  min-height: 1px;
  position: relative;
  flex: 1;
  background-color: ${props => props.theme.palette.neutral.default.two};
`

const FlightLineDots = styled(Group)`
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  height: ${rem(8)};
`

const FlightLineDot = styled.div`
  width: ${rem(8)};
  height: ${rem(8)};
  background-color: ${props => props.theme.palette.neutral.default.two};
  border-radius: 50%;
  border: 1px solid ${props => props.theme.palette.neutral.default.eight};
`

const NextDayIndicator = styled.sup`
  position: relative;
  display: inline-block;
  font-size: 0.6em;
  margin-left: ${rem(2)};
`

interface Props {
  departureTime?: string
  arrivalTime?: string
  arrivalDate?: string
  departureDate?: string
  departureAirport: string
  arrivalAiport: string
  duration?: string
  stops: number
  showDate?: boolean
}

function FareFamilyFlightTileV2TimeLine(props: Props) {
  const { departureAirport, departureTime, arrivalAiport, arrivalTime, duration, stops, arrivalDate, departureDate, showDate = true } = props
  const intl = useIntl()

  const stopsArray = useMemo(() => {
    return fillArray(stops)
  }, [stops])

  // Calculate number of days ahead for arrival
  const daysAhead = useMemo(() => {
    if (!departureDate || !arrivalDate) {
      return 0
    }
    const departMoment = moment(departureDate)
    const arriveMoment = moment(arrivalDate)
    return arriveMoment.diff(departMoment, 'days')
  }, [departureDate, arrivalDate])

  const nextDayLabel = intl.formatMessage(
    {
      id: 'Ku95FPFA4Vw52WcByqTw',
      defaultMessage: '+{days}',
      description: 'Indicator showing number of days ahead for overnight flights, e.g. +1',
    },
    { days: daysAhead },
  )

  return (
    <TimeLineContainer>
      <Group direction="vertical">
        <Caption variant="large">{departureAirport}</Caption>
        <Heading variant="heading6">{departureTime}</Heading>
        {showDate && departureDate && <Caption variant="large">{departureDate}</Caption>}
      </Group>

      <Group direction="vertical" horizontalAlign="center">

        <FlightInfoContainer direction="horizontal" gap="0 2" verticalAlign="center">
          <FlightLineContainer direction="vertical" horizontalAlign="center" gap="8 0">
            <CaptionTextBlock variant="medium">{duration}</CaptionTextBlock>

            <FlightLine>
              <FlightLineDots direction="horizontal" gap="0 24">
                {stopsArray.map((_, i) => <FlightLineDot key={i} />)}
              </FlightLineDots>
            </FlightLine>

            {stops > 0 && <CaptionTextBlock variant="medium">
              {intl.formatMessage({
                id: 'zjq-T_fCULjcaWWSHOLK',
                defaultMessage: '{count, plural, one {# stop} other {# stops}}',
                description: 'Number of stops in flight timeline in the flight search or booking flow',
              }, { count: stops })}
            </CaptionTextBlock>}

            {stops === 0 && <CaptionTextBlock variant="medium" colour="highlight-secondary">
              {intl.formatMessage({
                id: '_1-yGA_edHlstfRILOgn',
                defaultMessage: 'Direct',
                description: 'Label indicating a non-stop direct flight in the flights flow',
              })}
            </CaptionTextBlock>}
          </FlightLineContainer>

          <SolidPlaneDepartureAltIcon size="S" />
        </FlightInfoContainer>
      </Group>

      <Group direction="vertical">
        <Caption variant="large">{arrivalAiport}</Caption>
        <Heading variant="heading6" style={{ position: 'relative' }}>
          {arrivalTime}
          {daysAhead > 0 && !showDate && <NextDayIndicator>{nextDayLabel}</NextDayIndicator>}
        </Heading>
        {showDate && arrivalDate && <Caption variant="large">{arrivalDate}</Caption>}
      </Group>
    </TimeLineContainer>
  )
}

export default FareFamilyFlightTileV2TimeLine
